import { useEffect, useState, useRef } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { ChatMessage } from '@/types/chat'
import { getWebSocketUrl } from '@/utils/websocket'

interface ChatWindowProps {
  eventId: string
  onPresentReceived?: (data: any) => void
}

function ChatWindow({ eventId, onPresentReceived }: ChatWindowProps) {
  const { user } = useAuth()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputMessage, setInputMessage] = useState('')
  const [isConnected, setIsConnected] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const maxReconnectAttempts = 3

  useEffect(() => {
    // 既に接続中の場合はスキップ
    if (wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING) {
      return
    }

    connectWebSocket()

    return () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close(1000, 'Component unmounting')
        wsRef.current = null
      }
    }
  }, [eventId])

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const connectWebSocket = () => {
    // 環境に応じたWebSocket URLを生成
    const wsUrl = getWebSocketUrl(`/api/v1/ws/events/${eventId}/chat`)
    const tokens = localStorage.getItem('auth_tokens')
    const accessToken = tokens ? JSON.parse(tokens).access_token : ''

    const ws = new WebSocket(`${wsUrl}?token=${accessToken}`)

    ws.onopen = () => {
      console.log('WebSocket connected')
      setIsConnected(true)
      reconnectAttemptsRef.current = 0 // リセット

      // 接続成功時にjoinメッセージを送信
      if (user) {
        const joinMessage = {
          type: 'user_joined',
          user_id: user.id,
          user_name: user.username || user.full_name || 'Unknown'
        }
        ws.send(JSON.stringify(joinMessage))
      }
    }

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)

        if (data.type === 'chat_message') {
          const message: ChatMessage = {
            id: data.message_id || Date.now().toString(),
            event_id: eventId,
            user_id: data.user_id,
            user_name: data.user_name,
            message: data.content,
            timestamp: data.timestamp || new Date().toISOString(),
            type: 'text',
          }
          setMessages(prev => [...prev, message])
        } else if (data.type === 'user_joined') {
          const systemMessage: ChatMessage = {
            id: Date.now().toString(),
            event_id: eventId,
            user_id: 'system',
            user_name: 'システム',
            message: `${data.user_name}が参加しました`,
            timestamp: new Date().toISOString(),
            type: 'system',
          }
          setMessages(prev => [...prev, systemMessage])
        } else if (data.type === 'user_left') {
          const systemMessage: ChatMessage = {
            id: Date.now().toString(),
            event_id: eventId,
            user_id: 'system',
            user_name: 'システム',
            message: `${data.user_name}が退出しました`,
            timestamp: new Date().toISOString(),
            type: 'system',
          }
          setMessages(prev => [...prev, systemMessage])
        } else if (data.type === 'present_notification' || (data.present_id && data.present_name)) {
          // プレゼント通知を親コンポーネントに伝える
          console.log('🎁 Received present notification in ChatWindow:', data)
          if (onPresentReceived) {
            onPresentReceived(data)
          }
        }
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error)
      }
    }

    ws.onerror = (error) => {
      console.error('WebSocket error:', error)
      setIsConnected(false)
    }

    ws.onclose = (event) => {
      console.log('WebSocket disconnected:', event.code, event.reason)
      setIsConnected(false)

      // 正常終了(1000)または最大試行回数を超えた場合は再接続しない
      if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
        reconnectAttemptsRef.current++
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 10000)
        console.log(`Reconnecting in ${delay}ms... (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`)
        setTimeout(() => {
          if (wsRef.current?.readyState === WebSocket.CLOSED) {
            connectWebSocket()
          }
        }, delay)
      } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
        console.log('Max reconnection attempts reached')
      }
    }

    wsRef.current = ws
  }

  const sendMessage = () => {
    if (!inputMessage.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return
    }

    const messageData = {
      type: 'chat_message',
      content: inputMessage,
      message_type: 'text',
      user_id: user?.id,
      user_name: user?.username,
    }

    wsRef.current.send(JSON.stringify(messageData))
    setInputMessage('')
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div className="h-full flex">
      {/* Messages Section */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="px-4 py-2 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="font-semibold text-gray-900">チャット</h3>
            <p className="text-sm text-gray-500">
              {isConnected ? (
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-500 rounded-full" />
                  接続中
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-gray-400 rounded-full" />
                  接続中...
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Input - Moved to top */}
        <div className="border-b p-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="メッセージを入力..."
              className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={!isConnected}
            />
            <button
              onClick={sendMessage}
              disabled={!isConnected || !inputMessage.trim()}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-700"
            >
              送信
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2 no-scrollbar">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`${
              msg.type === 'system'
                ? 'text-center text-sm text-gray-500 italic'
                : msg.user_id === user?.id
                ? 'text-right'
                : 'text-left'
            }`}
          >
            {msg.type === 'system' ? (
              <p>{msg.message}</p>
            ) : (
              <div
                className={`inline-block max-w-[80%] ${
                  msg.user_id === user?.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 text-gray-900'
                } rounded-lg px-3 py-2`}
              >
                {msg.user_id !== user?.id && (
                  <p className="text-xs font-semibold mb-1 opacity-80">
                    {msg.user_name}
                  </p>
                )}
                <p className="text-sm break-words">{msg.message}</p>
                <p className="text-xs mt-1 opacity-70">
                  {formatTime(msg.timestamp)}
                </p>
              </div>
            )}
          </div>
        ))}
          <div ref={messagesEndRef} />
        </div>
      </div>
    </div>
  )
}

export default ChatWindow